/**
 * 堡垒锻造的 Web Audio API provider。
 *
 * 使用振荡器和噪声缓冲区生成程序化音效——无需外部音频文件。
 * 实现了来自 @bastion/game-core 的 AudioProvider 接口。
 *
 * 支持的声音 ID（映射到合成的特效）：
 *   - build_tower    : 上升铃声
 *   - upgrade_tower  : 双铃
 *   - kill_enemy     : 短促冲击
 *   - kill_boss      : 深沉爆炸
 *   - wave_complete  : 胜利琶音
 *   - game_won       : 长号角
 *   - game_lost      : 下降失败音调
 *   - skill_used     : 呼啸
 *   - skill_fire     : 火焰爆发
 *   - skill_frost    : 冰晶闪烁
 */

import type { AudioProvider } from "@bastion/game-core";

// ─── AudioContext 缓存 ────────────────────────────────────────────────────

let _ctx: AudioContext | null = null;
let _dest: AudioNode | null = null;
let _masterGain: GainNode | null = null;
let _masterVolume = 1;
let _contextFactory: (() => AudioContext) | null = null;

/** 测试注入；传 null 恢复浏览器 AudioContext factory。 */
export function setAudioContextFactoryForTests(factory: (() => AudioContext) | null): void {
  _contextFactory = factory;
  _ctx = null;
  _dest = null;
  _masterGain = null;
  _masterVolume = 1;
}

function createAudioContext(): AudioContext {
  if (_contextFactory) return _contextFactory();
  const AudioContextConstructor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  return new AudioContextConstructor();
}

function getContext(): AudioContext {
  if (!_ctx) {
    _ctx = createAudioContext();
    _masterGain = _ctx.createGain();
    _masterGain.gain.value = _masterVolume;
    _masterGain.connect(_ctx.destination);
    _dest = _masterGain;
  }
  if (_ctx.state === "suspended") {
    _ctx.resume();
  }
  return _ctx;
}

// ─── 声音生成器 ──────────────────────────────────────────────────────

/** 播放单个频率为 freq、持续 duration 秒的音调。 */
function tone(freq: number, duration: number, type: OscillatorType = "sine", vol = 0.15): void {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  gain.gain.setValueAtTime(vol, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  osc.connect(gain).connect(_dest!);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
}

/** 播放短促噪声脉冲（冲击/爆炸效果）。 */
function noise(duration: number, vol = 0.15): void {
  const ctx = getContext();
  const now = ctx.currentTime;
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(vol, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  src.connect(gain).connect(_dest!);
  src.start(now);
  src.stop(now + duration);
}

/** 在绝对时间 offset 处播放噪声脉冲，用于 AudioContext 时间轴调度。 */
function noiseAt(offset: number, duration: number, vol = 0.15): void {
  const ctx = getContext();
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(vol, offset);
  gain.gain.exponentialRampToValueAtTime(0.001, offset + duration);
  src.connect(gain).connect(_dest!);
  src.start(offset);
  src.stop(offset + duration);
}

/**
 * 播放带频率扫动的噪声。
 *
 * startFreq / endFreq 控制噪声中心频率，
 * 适合制作箭矢、子弹、风声等快速移动音效。
 */
function sweepNoise(duration: number, startFreq: number, endFreq: number, vol = 0.15): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  const bufferSize = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  // 白噪声
  for (let i = 0; i < bufferSize; i++) {
    const t = i / bufferSize;

    // 平滑衰减，避免过于生硬
    const envelope = Math.pow(1 - t, 1.8);

    data[i] = (Math.random() * 2 - 1) * envelope;
  }

  const src = ctx.createBufferSource();
  src.buffer = buffer;

  // 用带通滤波器把白噪声变成“空气感”
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";

  filter.frequency.setValueAtTime(startFreq, now);
  filter.frequency.exponentialRampToValueAtTime(endFreq, now + duration);

  filter.Q.setValueAtTime(1.2, now);

  const gain = ctx.createGain();

  gain.gain.setValueAtTime(vol, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  src.connect(filter).connect(gain).connect(_dest!);

  src.start(now);
  src.stop(now + duration);
}

/** 播放下降扫频（失败效果）。 */
function sweepDown(startFreq: number, endFreq: number, duration: number, vol = 0.12): void {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(startFreq, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(endFreq, ctx.currentTime + duration);
  gain.gain.setValueAtTime(vol, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  osc.connect(gain).connect(_dest!);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
}

/** 播放频率上升（技能呼啸效果）。 */
function rampUp(startFreq: number, endFreq: number, duration: number, vol = 0.1): void {
  const ctx = getContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(startFreq, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(endFreq, ctx.currentTime + duration);
  gain.gain.setValueAtTime(vol, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  osc.connect(gain).connect(_dest!);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
}

// ─── 每个声音的实现 ──────────────────────────────────────────────────

function playBuildTower(): void {
  tone(523, 0.1, "sine", 0.12); // C5
  setTimeout(() => tone(659, 0.12, "sine", 0.12), 80); // E5
}

function playUpgradeTower(): void {
  tone(523, 0.08, "sine", 0.12);
  setTimeout(() => tone(659, 0.08, "sine", 0.12), 60);
  setTimeout(() => tone(784, 0.15, "sine", 0.12), 120);
}

function playKillEnemy(): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  // ① 击杀冲击——短促噪声，敌人倒地的闷响
  noise(0.06, 0.2);

  // ② 倒地嗡鸣——极低频正弦快速衰减，模拟身体砸向地面
  const thud = ctx.createOscillator();
  const thudGain = ctx.createGain();
  thud.type = "sine";
  thud.frequency.setValueAtTime(120, now);
  thud.frequency.exponentialRampToValueAtTime(50, now + 0.15);
  thudGain.gain.setValueAtTime(0, now);
  thudGain.gain.linearRampToValueAtTime(0.18, now + 0.01);
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
  thud.connect(thudGain).connect(_dest!);
  thud.start(now);
  thud.stop(now + 0.16);

  // ③ 低频尾韵——延迟 30ms，一个更轻的嗡鸣延续，给倒地添一点分量感
  noiseAt(now + 0.03, 0.08, 0.1);
}

function playKillBoss(): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  // ① 爆裂——强力噪声爆发，Boss 被击杀的震撼感
  noise(0.2, 0.3);

  // ② 主低音——极低沉的正弦持续坠落，像巨兽倒下
  const boom = ctx.createOscillator();
  const boomGain = ctx.createGain();
  boom.type = "sine";
  boom.frequency.setValueAtTime(80, now);
  boom.frequency.exponentialRampToValueAtTime(30, now + 0.4);
  boomGain.gain.setValueAtTime(0, now);
  boomGain.gain.linearRampToValueAtTime(0.28, now + 0.02);
  boomGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  boom.connect(boomGain).connect(_dest!);
  boom.start(now);
  boom.stop(now + 0.45);

  // ③ 次声震——延迟 100ms 的二次冲击，像余震
  noiseAt(now + 0.1, 0.15, 0.15);
  const aftershock = ctx.createOscillator();
  const aftershockGain = ctx.createGain();
  aftershock.type = "sine";
  aftershock.frequency.setValueAtTime(55, now + 0.1);
  aftershockGain.gain.setValueAtTime(0, now + 0.1);
  aftershockGain.gain.linearRampToValueAtTime(0.15, now + 0.12);
  aftershockGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
  aftershock.connect(aftershockGain).connect(_dest!);
  aftershock.start(now + 0.1);
  aftershock.stop(now + 0.3);

  // ④ 余韵——延迟 250ms 的一声极低频叹息，像 Boss 最后的呼吸
  noiseAt(now + 0.25, 0.2, 0.08);
  const breath = ctx.createOscillator();
  const breathGain = ctx.createGain();
  breath.type = "sine";
  breath.frequency.setValueAtTime(45, now + 0.25);
  breathGain.gain.setValueAtTime(0, now + 0.25);
  breathGain.gain.linearRampToValueAtTime(0.1, now + 0.27);
  breathGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  breath.connect(breathGain).connect(_dest!);
  breath.start(now + 0.25);
  breath.stop(now + 0.5);
}

function playWaveComplete(): void {
  const notes = [523, 659, 784, 1047];
  notes.forEach((f, i) => {
    setTimeout(() => tone(f, 0.15, "sine", 0.12), i * 100);
  });
}

function playGameWon(): void {
  const melody = [523, 659, 784, 1047, 784, 1047];
  melody.forEach((f, i) => {
    setTimeout(() => tone(f, 0.2, "sine", 0.12), i * 120);
  });
}

function playGameLost(): void {
  sweepDown(400, 80, 0.6, 0.12);
  setTimeout(() => noise(0.2, 0.15), 300);
}

function playSkillUsed(): void {
  rampUp(300, 900, 0.15, 0.1);
  noise(0.1, 0.1);
}

function playSkillFire(): void {
  noise(0.2, 0.2);
  tone(150, 0.15, "sawtooth", 0.1);
  setTimeout(() => tone(200, 0.1, "square", 0.08), 50);
}

function playSkillFrost(): void {
  tone(1200, 0.3, "sine", 0.08);
  tone(1600, 0.25, "sine", 0.06);
  setTimeout(() => tone(1400, 0.2, "sine", 0.06), 80);
}

// ─── 炮台子弹音效 ──────────────────────────────────────────────────────

/**
 * 箭塔「啾」：单一短促清脆声。
 * 底层三角形波快速上行 + 噪声脉冲模拟破空，叠加一个高频泛音增添明亮感。
 */
function playTowerArrow(): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  // ① 弓弦释放的瞬间
  noise(0.012, 0.06);

  // ② 箭矢破空
  sweepNoise(0.085, 4200, 1100, 0.11);

  // ③ 很轻的主体音，让玩家明确感知“发射”
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = "triangle";

  osc.frequency.setValueAtTime(900, now);
  osc.frequency.exponentialRampToValueAtTime(1800, now + 0.055);

  gain.gain.setValueAtTime(0.09, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.075);

  osc.connect(gain).connect(_dest!);

  osc.start(now);
  osc.stop(now + 0.075);

  // ④ 高频尾音
  const tail = ctx.createOscillator();
  const tailGain = ctx.createGain();

  tail.type = "sine";
  tail.frequency.setValueAtTime(2700, now + 0.065);

  tailGain.gain.setValueAtTime(0.025, now + 0.065);

  tailGain.gain.exponentialRampToValueAtTime(0.001, now + 0.105);

  tail.connect(tailGain).connect(_dest!);

  tail.start(now + 0.065);
  tail.stop(now + 0.105);
}

/**
 * 炮塔「咚」：炮弹发射声。单一连贯的沉闷轰鸣，不拆成两段。
 * ① 炮口爆震——尖锐噪声瞬间触发，给出"开火"的打击感
 * ② 弹体重击——正弦 100→60Hz 快滑降，方波叠加谐波厚度
 * 三者在时间上完全重叠，构成一声"咚"
 */
function playTowerCannon(): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  // ① 炮口爆震——极短噪声，给出打击的瞬态冲击
  noise(0.03, 0.35);

  // ② 弹体重击——正弦 100→60Hz，120ms 快衰，是"咚"的主体
  const body = ctx.createOscillator();
  const bodyGain = ctx.createGain();
  body.type = "sine";
  body.frequency.setValueAtTime(100, now);
  body.frequency.exponentialRampToValueAtTime(60, now + 0.12);
  bodyGain.gain.setValueAtTime(0, now);
  bodyGain.gain.linearRampToValueAtTime(0.28, now + 0.008);
  bodyGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
  body.connect(bodyGain).connect(_dest!);
  body.start(now);
  body.stop(now + 0.14);

  // ③ 方波谐波层——叠加在正弦之上，让"咚"更有金属质感
  const clang = ctx.createOscillator();
  const clangGain = ctx.createGain();
  clang.type = "square";
  clang.frequency.setValueAtTime(100, now);
  clang.frequency.exponentialRampToValueAtTime(60, now + 0.12);
  clangGain.gain.setValueAtTime(0, now);
  clangGain.gain.linearRampToValueAtTime(0.08, now + 0.008);
  clangGain.gain.exponentialRampToValueAtTime(0.001, now + 0.13);
  clang.connect(clangGain).connect(_dest!);
  clang.start(now);
  clang.stop(now + 0.13);
}

/**
 * 雷塔「滋」：电流放电声。四层叠加，不对称、不规则。
 * ① 主电弧：锯齿波高频骤降，带轻微失真感，模拟高压电弧撕裂空气
 * ② 放电噼啪：随机间隔的噪声脉冲，模拟电火花飞溅
 * ③ 低频嗡鸣：正弦持续铺垫，给电流"蓄能感"
 * ④ 高频嘶鸣：极短的超高频脉冲，像绝缘体击穿时的尖啸
 */
function playTowerThunder(): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  // ① 主电弧——锯齿波从极高频率骤降到中频，像一道闪电劈下
  const arc = ctx.createOscillator();
  const arcGain = ctx.createGain();
  arc.type = "sawtooth";
  arc.frequency.setValueAtTime(3500, now);
  arc.frequency.exponentialRampToValueAtTime(500, now + 0.1);
  arcGain.gain.setValueAtTime(0.15, now);
  arcGain.gain.exponentialRampToValueAtTime(0.001, now + 0.11);
  arc.connect(arcGain).connect(_dest!);
  arc.start(now);
  arc.stop(now + 0.11);

  // ② 放电噼啪——两段随机间隔的噪声脉冲（0ms / 55ms），模拟火花飞溅
  noise(0.04, 0.12);
  noiseAt(now + 0.055, 0.03, 0.1);

  // ③ 低频嗡鸣——正弦持续铺垫，像变压器蓄能
  const hum = ctx.createOscillator();
  const humGain = ctx.createGain();
  hum.type = "sine";
  hum.frequency.setValueAtTime(120, now);
  humGain.gain.setValueAtTime(0, now);
  humGain.gain.linearRampToValueAtTime(0.08, now + 0.04);
  humGain.gain.setValueAtTime(0.08, now + 0.07);
  humGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  hum.connect(humGain).connect(_dest!);
  hum.start(now);
  hum.stop(now + 0.15);

  // ④ 高频嘶鸣——极短促的方波尖啸，延迟 10ms，像绝缘体被击穿
  const shriek = ctx.createOscillator();
  const shriekGain = ctx.createGain();
  shriek.type = "square";
  shriek.frequency.setValueAtTime(5000, now + 0.01);
  shriekGain.gain.setValueAtTime(0, now + 0.01);
  shriekGain.gain.setValueAtTime(0.04, now + 0.02);
  shriekGain.gain.exponentialRampToValueAtTime(0.001, now + 0.045);
  shriek.connect(shriekGain).connect(_dest!);
  shriek.start(now + 0.01);
  shriek.stop(now + 0.045);
}

/**
 * 冰塔「叮」：冰锥发射声。四层叠加，晶莹剔透。
 * ① 主音——正弦快速上行，像冰锥破空而出
 * ② 泛音层——更高频的正弦，延迟叠加，增添冰晶闪烁的明亮感
 * ③ 碎裂层——延迟噪声，模拟冰锥撞击时的碎裂
 * ④ 尾韵——极轻的高频正弦缓慢衰减，像寒气消散
 */
function playTowerIce(): void {
  const ctx = getContext();
  const now = ctx.currentTime;

  // ① 主音——正弦 1800→2400Hz 上行，60ms，冰锥破空
  const main = ctx.createOscillator();
  const mainGain = ctx.createGain();
  main.type = "sine";
  main.frequency.setValueAtTime(1800, now);
  main.frequency.exponentialRampToValueAtTime(2400, now + 0.06);
  mainGain.gain.setValueAtTime(0.1, now);
  mainGain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
  main.connect(mainGain).connect(_dest!);
  main.start(now);
  main.stop(now + 0.07);

  // ② 泛音层——2800Hz 极轻正弦，40ms 后进入，增添闪烁感
  const shimmer = ctx.createOscillator();
  const shimmerGain = ctx.createGain();
  shimmer.type = "sine";
  shimmer.frequency.setValueAtTime(2800, now + 0.04);
  shimmerGain.gain.setValueAtTime(0, now + 0.04);
  shimmerGain.gain.setValueAtTime(0.05, now + 0.045);
  shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  shimmer.connect(shimmerGain).connect(_dest!);
  shimmer.start(now + 0.04);
  shimmer.stop(now + 0.12);

  // ③ 碎裂层——延迟 50ms 的脆响噪声，像冰锥砸碎
  noiseAt(now + 0.05, 0.08, 0.08);

  // ④ 尾韵——60ms 后一声极轻的高频正弦，像寒气消散
  const tail = ctx.createOscillator();
  const tailGain = ctx.createGain();
  tail.type = "sine";
  tail.frequency.setValueAtTime(3200, now + 0.06);
  tailGain.gain.setValueAtTime(0, now + 0.06);
  tailGain.gain.setValueAtTime(0.02, now + 0.065);
  tailGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  tail.connect(tailGain).connect(_dest!);
  tail.start(now + 0.06);
  tail.stop(now + 0.18);
}

// ─── 注册表 ───────────────────────────────────────────────────────────

const PLAYERS: Record<string, () => void> = {
  build_tower: playBuildTower,
  upgrade_tower: playUpgradeTower,
  kill_enemy: playKillEnemy,
  kill_boss: playKillBoss,
  wave_complete: playWaveComplete,
  game_won: playGameWon,
  game_lost: playGameLost,
  skill_used: playSkillUsed,
  skill_fire: playSkillFire,
  skill_frost: playSkillFrost,
  tower_arrow: playTowerArrow,
  tower_cannon: playTowerCannon,
  tower_thunder: playTowerThunder,
  tower_ice: playTowerIce,
};

// ─── Provider 实现 ────────────────────────────────────────────────────

/** 每种音效两次播放之间的最短间隔（毫秒），防止齐射时声音叠加过密。 */
const COOLDOWN_MS: Record<string, number> = {
  tower_arrow: 150, // 箭塔攻击间隔约 833ms，远大于 150ms
  tower_cannon: 250, // 炮塔攻击间隔约 2s，250ms 足够过滤齐射噪音
  tower_thunder: 180, // 雷塔攻击间隔约 1.25s
  tower_ice: 150, // 冰塔攻击间隔约 1s
  // 其他音效保持较快刷新
  skill_used: 100,
  skill_fire: 200,
  skill_frost: 200,
  kill_enemy: 120,
};

export class WebAudioProvider implements AudioProvider {
  private _lastPlayTime: Record<string, number> = {};

  /** 必须从用户手势同步调用，以解锁浏览器的自动播放策略。 */
  async unlock(): Promise<boolean> {
    try {
      const context = getContext();
      if (context.state === "suspended") await context.resume();
      return context.state === "running";
    } catch {
      return false;
    }
  }

  isActive(): boolean {
    try {
      if (typeof window === "undefined") return _contextFactory !== null;
      const webkitContext = (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
      return _contextFactory !== null || !!window.AudioContext || !!webkitContext;
    } catch {
      return false;
    }
  }

  play(soundId: string, _volume?: number): number | undefined {
    if (!this.isActive()) return undefined;
    const player = PLAYERS[soundId];
    if (!player) return undefined;
    // 冷却检查：同种音效在最短间隔内只播放一次，避免多塔齐射时声音叠加
    const now = Date.now();
    const cooldown = COOLDOWN_MS[soundId] ?? 0;
    if (cooldown > 0 && now - this._lastPlayTime[soundId] < cooldown) return undefined;
    this._lastPlayTime[soundId] = now;
    try {
      player();
      return now;
    } catch {
      return undefined;
    }
  }

  stop(_soundId: string): void {
    // 程序化音效是一次性发射；无持久句柄
  }

  setMasterVolume(volume: number): void {
    _masterVolume = Math.max(0, Math.min(1, volume));
    if (_masterGain && _ctx) {
      _masterGain.gain.setTargetAtTime(_masterVolume, _ctx.currentTime, 0.015);
    }
  }
}
